/// <reference path="../../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppVoiceRecorder } from '../../components';
import { Label, MessageBar, MessageBarType } from 'office-ui-fabric-react';
import { getRecordingContainerStyles, getWarningMessageBarStyles } from './Recording.styles';
import { SetForegroundTaskInStore } from '../../actions/LoadingActions';
import { RouteComponentProps } from 'react-router-dom';
import { TranscriptionRoutePathNames } from '../../routes';
import { LiveRecordingSession } from '../../../backend/LiveRecordingSession';
import recordingStore from '../../stores/RecordingStore';
import { ChangeEvent } from '../../stores/utils';
import { SetRecorderIsPausedInStore, SetRecorderReadyInStore, SetMicrophoneStateInStore, MicrophoneState, SetWarningDismissedInStore } from '../../actions/RecordingActions';
import { AppLayoutSettings } from '../common.styles';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { LogError } from '../../../utils/ErrorTelemetry';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import { TaskList, TaskListResultStatus } from '../../../backend/TaskList';
import { SetFailureStateAndRouteToFailure, FailureContinuation } from '../../actions/FailureActions';
import { CheckNetworkConnection } from '../../NetworkConnectionMonitor';
import { NetworkError, NetworkErrors } from '../../../utils/Errors';
import { RealTimeTranscriptionConnectionError, RealTimeTranscriptionConnectionErrors } from '../../../backend/RealTimeTranscriptionConnectionErrors';
import { LiveRecordingKeys } from '../../../backend/telemetry/LiveRecordingKeys';
import { LiveRecordingForegroundTask } from '../../../backend/LiveRecordingForegroundTask';
import { AppAuthenticationProviderContext } from '../../context';
import { ErrorStringFromException } from '../../../utils/utils';

export interface RecordingState {
  isRecorderReady: boolean;
  isPaused: boolean;
  microphoneState: MicrophoneState;
  warningDismissed: boolean;
}

export class Recording extends React.PureComponent<RouteComponentProps, RecordingState> {
  private session: LiveRecordingSession;
  private initRecordingTaskList: TaskList<any>;
  private _isMounted: boolean;
  private isAudioBackAvailable: boolean;
  static contextType = AppAuthenticationProviderContext;

  constructor(props: RouteComponentProps) {
    super(props);
    this._isMounted = false;
    this.isAudioBackAvailable = false;

    this.state = recordingStore.getState();
    recordingStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public async UNSAFE_componentWillMount() {
    try {
      LogEvents.LiveRecording.Start();
      LogEvents.LiveRecording.BeginDurationMeasure(DurationMeasureType.Normal, LiveRecordingKeys.InitializationTotalDuration);
      CheckNetworkConnection();
      if(this.props.location.state.audioBackup == null) {
        throw new Error('audioBackup is null inside Recording component');
      }

      if(this.props.location.state.realTimeTranscriptionConnection == null) {
        throw new Error('realTimeTranscriptionConnection is null inside Recording component');
      }

      this.isAudioBackAvailable = this.props.location.state.audioBackup.IsInitializedSuccessfully();
      this.session = new LiveRecordingSession(
        this.props.location.state.realTimeTranscriptionConnection, 
        (error: Error) => this.onErrorCallback(error),
        this.props.location.state.audioBackup);

      this.props.location.state.audioBackup = null;
      this.props.location.state.realTimeTranscriptionConnection = null;

      this.initRecordingTaskList = new TaskList([
        () => this.session.Initialize(),
        () => this.InitializeMicrophoneState()
      ]);

      const result = await this.initRecordingTaskList.ExecuteTaskListSequentially();

      if (result.status == TaskListResultStatus.Canceled) {
        await this.session.Pause();
        return;
      }

      SetRecorderReadyInStore(true);
      LogEvents.LiveRecording.EndDurationMeasure(LiveRecordingKeys.InitializationTotalDuration);
    } catch (e) {
      let failureMessage = TranscriptionStrings.UnknownRecordingFailureReason;
      if (!this.isAudioBackAvailable) {
        failureMessage = TranscriptionStrings.UnknownInitFailureReason;
      }

      let failureContinuation = FailureContinuation.StartOver;

      if (e instanceof NetworkError && e.name == NetworkErrors.NoInternetConnection) {
        failureMessage = TranscriptionStrings.ConnectionIssueFailureReason;
        failureContinuation = FailureContinuation.Retry;
      }

      const failureReasonDebug: string = 'Recording componentWillMount threw error : ' + ErrorStringFromException(e);
      await this.handleFailure(failureMessage, failureContinuation, failureReasonDebug, history);
    }
  }

  public async componentDidMount() {
    this._isMounted = true;
  }

  public async componentWillUnmount() {
    this.initRecordingTaskList.Cancel();

    if (this.state.isRecorderReady)
      await this.session.Pause();
    
    this._isMounted = false;
  }

  public render() {
    const onRecordingStatusChange = async (isPaused: boolean) => {
      try {
        if (isPaused)
          await this.session.Pause();
        else
          await this.session.Resume();
      } catch (err) {
        await this.handleFailure(
          TranscriptionStrings.UnknownInitFailureReason, 
          FailureContinuation.None /*failureContinuation*/, 
          'Recording render threw error: ' + ErrorStringFromException(err), 
          this.props.history);
      }

      SetRecorderIsPausedInStore(isPaused);
    }

    const onRecordingDone = (): void => {
      LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Transcribe now" }]);
      LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
      SetForegroundTaskInStore(new LiveRecordingForegroundTask(this.session, this.context, this.props.history));
      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    }

    const enableRecorder = this.state.isRecorderReady && this.state.microphoneState == MicrophoneState.Enabled;

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <Label styles={AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx,AppLayoutSettings.AppColors.darkLabelColor)}>{TranscriptionStrings.CompleteYourRecordingHeading}</Label>
        {enableRecorder ? <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.RecordingInfoMessage}</Label> : null}
        <div style={getRecordingContainerStyles()}>
          <AppVoiceRecorder isEnabled={enableRecorder} type={'AppVoiceRecorderProps'} recordingStatusLabel={this.getRecordingLabel()} stopRecordingButtonLabel={TranscriptionStrings.TranscribeNowButtonLabel} onRecordingStop={onRecordingDone} onRecordingStatusChange={onRecordingStatusChange} />
        </div>
        { this.isAudioBackAvailable || this.state.warningDismissed ? null :
          <div style={getWarningMessageBarStyles()}> 
            <MessageBar 
              messageBarType={MessageBarType.warning} 
              dismissButtonAriaLabel={TranscriptionStrings.CloseLabel}
              onDismiss={()=>{SetWarningDismissedInStore()}}
              isMultiline={true}
              >
              <Label styles={AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx,AppLayoutSettings.AppColors.darkLabelColor)}>
                {TranscriptionStrings.RecordingBackupWarningTitleLabel}
              </Label>
              <Label styles={AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx,AppLayoutSettings.AppColors.mediumLabelColor)}>
                {TranscriptionStrings.RecordingBackupWarningBodyLabel}
              </Label>
            </MessageBar>
          </div>
        }
      </div>
    );
  }

  // There are three states we are in when calling getUserMedia to request mic access:
  // 1. Not called
  // 2. Called but pending
  // 3. Approved/Rejected
  // If the browser does not support the permissions API, in #2 we are forced to assume the user hasn't given us permissions, so we show "Please give us permissions". 
  // However, it is possible that the user had already given use permissions before and the time we were in #2 was just the time it took for the mic to initialize, 
  // which will result in showing "Please give us permissions" briefly.
  // If the browser supports the permissions API, we can preemptively know they have already given us permissions and avoid showing "Please give us permissions" at all
  private async InitializeMicrophoneState() {
    LogEvents.LiveRecording.BeginDurationMeasure(DurationMeasureType.Normal, LiveRecordingKeys.MicrophoneInitializationDuration);
    // Preemptively check if we have mic permissions if the browser supports permissions API.
    let permissionStatus = null
    const navigatorAny: any = navigator;
    if (navigatorAny.permissions) {
      try {
        permissionStatus = await navigatorAny.permissions.query({ name: 'microphone' });
      } catch (_e) {
        // The browser doesn't support the API, so no-op and fall back to assuming permissions weren't granted
      }
    }

    // If we know that we have mic permissions, keep "Setting up the recording..." state until mic is initialized.
    // This prevents us from briefly showing an "Enable microphone permissions to record" message when we know we have permissions.
    const isMicPermissionGranted = permissionStatus != null && permissionStatus.state == "granted";
    if (!isMicPermissionGranted) {
      SetRecorderReadyInStore(true);
    }

    if (permissionStatus != null) {
      LogEvents.LiveRecording.AddData([{ key: LiveRecordingKeys.MicrophonePremissionsPreviouslyGranted, value: isMicPermissionGranted }]);
    }

    try {
      await this.session.StartMicrophone();
      
      if (this.state.microphoneState == MicrophoneState.Pending)
        SetMicrophoneStateInStore(MicrophoneState.Enabled);

      LogEvents.LiveRecording.AddData([{ key: LiveRecordingKeys.MicrophoneSuccessfullyInitialized, value: true }]);
    } catch(e) {
      if (e instanceof RealTimeTranscriptionConnectionError && e.name == RealTimeTranscriptionConnectionErrors.MicrophonePermissionError)
        SetMicrophoneStateInStore(MicrophoneState.Disabled);
    }

    LogEvents.LiveRecording.EndDurationMeasure(LiveRecordingKeys.MicrophoneInitializationDuration);
  }

  private getRecordingLabel() {
    if (!this.state.isRecorderReady)
      return TranscriptionStrings.RecordingSettingUpLabel;

    if (this.state.microphoneState == MicrophoneState.Pending)
      return TranscriptionStrings.RecordingEnableMicPermissionsLabel;

    if (this.state.microphoneState == MicrophoneState.Disabled)
      return TranscriptionStrings.RecordingEnableMicPermissionsInBrowserLabel;

    // Recording Pause\Resume label has been removed, in latest UI specs.
    // So return undefined label for all the cases, except required ones like
    // Error scenarios, where label is showing mandatory information to user.
    return undefined;
  }

  private async handleFailure(failureReason: string, failureContinuation: FailureContinuation, failureReasonDebug: string, history: any) {
    LogError(LogEventNames.LiveRecording, failureReasonDebug);
    if (this.session) {
      await this.session.Close();
      SetForegroundTaskInStore(null);
      this.session = null;
    }

    LogEvents.LiveRecording.End([{ key: LiveRecordingKeys.FailureReason, value: failureReason }, { key: LiveRecordingKeys.SuccessfullyEnded, value: false }]);
    LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: failureReason, isFinalResponse: true });
    SetFailureStateAndRouteToFailure(LogEventNames.LiveRecording, failureReason, failureContinuation, history);
  }

  private async onErrorCallback(err: Error) {
    const failureReasonDebug: string = 'LiveRecordingSession threw error : ' + err.message;
    let failureMessageForUser: string = TranscriptionStrings.UnknownRecordingFailureReason;
    if (!this.isAudioBackAvailable) {
      failureMessageForUser = TranscriptionStrings.UnknownInitFailureReason;
    }

    await this.handleFailure(failureMessageForUser, FailureContinuation.None /*failureContinuation*/, failureReasonDebug, this.props.history);
  }

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(recordingStore.getState());
    }
  };
}